/**
 * Icon component library using Heroicons.
 * 
 * All icons use Heroicons outline style for consistency.
 * Source: https://heroicons.com/
 * 
 * Icons are standardized at 20x20 pixels with stroke width 1.5.
 * This library wraps @heroicons/react components to maintain a consistent interface.
 */

import React from 'react';
import {
  PencilIcon,
  TrashIcon as HeroTrashIcon,
  ArrowPathIcon,
  SunIcon as HeroSunIcon,
  MoonIcon as HeroMoonIcon,
  PlayIcon as HeroPlayIcon,
  PauseIcon as HeroPauseIcon,
  StopIcon as HeroStopIcon,
  ClipboardDocumentIcon,
  LockClosedIcon as HeroLockClosedIcon,
  ShieldCheckIcon as HeroShieldCheckIcon,
  HomeIcon as HeroHomeIcon,
  Cog6ToothIcon,
  WrenchScrewdriverIcon,
  QuestionMarkCircleIcon as HeroQuestionMarkCircleIcon,
  CloudIcon as HeroCloudIcon,
  MagnifyingGlassIcon as HeroMagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

interface IconProps {
  className?: string;
  width?: number;
  height?: number;
  color?: string;
}

/**
 * Wrapper component to standardize Heroicons with our IconProps interface
 */
const createIconWrapper = (
  IconComponent: React.ComponentType<React.SVGProps<SVGSVGElement>>
): React.FC<IconProps> => {
  const WrappedIcon: React.FC<IconProps> = ({ 
    className = '', 
    width = 20, 
    height = 20,
    color 
  }) => {
    const props: React.SVGProps<SVGSVGElement> = {
      className,
      width,
      height,
    };
    if (color) {
      props.style = { color };
    }
    return React.createElement(IconComponent, props);
  };
  return WrappedIcon;
};

/**
 * Edit/Pencil icon - Heroicons outline pencil
 * Used for: Editing user details, editing any entity
 */
export const EditIcon = createIconWrapper(PencilIcon);

/**
 * Delete/Trash icon - Heroicons outline trash
 * Used for: Deleting users, deleting cloud configurations, any delete action
 */
export const TrashIcon = createIconWrapper(HeroTrashIcon);

/**
 * Refresh icon - Heroicons outline arrow-path
 * Used for: Refreshing data, reloading content
 */
export const RefreshIcon = createIconWrapper(ArrowPathIcon);

/**
 * Sun icon - Heroicons outline sun
 * Used for: Light theme indicator
 */
export const SunIcon = createIconWrapper(HeroSunIcon);

/**
 * Moon icon - Heroicons outline moon
 * Used for: Dark theme indicator
 */
export const MoonIcon = createIconWrapper(HeroMoonIcon);

/**
 * Play icon - Heroicons outline play
 * Used for: Starting/playing VMs, media playback
 */
export const PlayIcon = createIconWrapper(HeroPlayIcon);

/**
 * Pause icon - Heroicons outline pause
 * Used for: Pausing VMs, media playback
 */
export const PauseIcon = createIconWrapper(HeroPauseIcon);

/**
 * Stop icon - Heroicons outline stop
 * Used for: Stopping VMs, media playback
 */
export const StopIcon = createIconWrapper(HeroStopIcon);

/**
 * Clipboard icon - Heroicons outline clipboard-document
 * Used for: Copying text to clipboard, copy actions
 */
export const ClipboardIcon = createIconWrapper(ClipboardDocumentIcon);

/**
 * Login icon - Heroicons outline lock-closed
 * Used for: Local login settings, security/authentication pages
 */
export const LoginIcon = createIconWrapper(HeroLockClosedIcon);

/**
 * OAuth icon - Heroicons outline shield-check
 * Used for: OAuth login settings, security/authentication pages
 */
export const OAuthIcon = createIconWrapper(HeroShieldCheckIcon);

/**
 * Home icon - Heroicons outline home
 * Used for: Projects/home page navigation
 */
export const HomeIcon = createIconWrapper(HeroHomeIcon);

/**
 * Admin icon - Heroicons outline cog-6-tooth
 * Used for: Admin navigation
 */
export const AdminIcon = createIconWrapper(Cog6ToothIcon);

/**
 * Wrench icon - Heroicons outline wrench-screwdriver
 * Used for: Settings page navigation
 */
export const WrenchIcon = createIconWrapper(WrenchScrewdriverIcon);

/**
 * Help icon - Heroicons outline question-mark-circle
 * Used for: Context help, tooltips, help popups
 */
export const HelpIcon = createIconWrapper(HeroQuestionMarkCircleIcon);

/**
 * OpenStack/Cloud icon - Heroicons outline cloud
 * Used for: OpenStack settings, cloud configuration pages
 */
export const OpenStackIcon = createIconWrapper(HeroCloudIcon);

/**
 * Search icon - Heroicons outline magnifying-glass
 * Used for: Search functionality, search bars
 */
export const SearchIcon = createIconWrapper(HeroMagnifyingGlassIcon);

/**
 * Kubernetes icon - Custom Kubernetes logo icon
 * Used for: Kubernetes cluster indicators, cluster-related UI elements
 */
export const KubernetesIcon: React.FC<IconProps> = ({ 
  className = '', 
  width = 20, 
  height = 20,
  color 
}) => {
  const props: React.SVGProps<SVGSVGElement> = {
    xmlns: 'http://www.w3.org/2000/svg',
    fill: 'none',
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    stroke: 'currentColor',
    className,
    width,
    height,
  };
  if (color) {
    props.style = { color };
  }
  return (
    <svg {...props}>
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        d="M12 2L19.8 5.8L21.7 14.2L16.3 21H7.7L2.3 14.2L4.2 5.8L12 2Z M12 2L12 8 M19.8 5.8L15.1 9.5 M21.7 14.2L15.9 12.9 M16.3 21L13.7 15.6 M7.7 21L10.3 15.6 M2.3 14.2L8.1 12.9 M4.2 5.8L8.9 9.5 M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" 
      />
    </svg>
  );
};
